import { validateModbusHost } from "./network/modbus-device-model";

export const S7_FIELD_TYPES = Object.freeze([
  { value: "BOOL", label: "BOOL", bytes: 1, tagType: "Boolean" },
  { value: "BYTE", label: "BYTE", bytes: 1, tagType: "64-bit integer" },
  { value: "WORD", label: "WORD", bytes: 2, tagType: "64-bit integer" },
  { value: "INT", label: "INT", bytes: 2, tagType: "64-bit integer" },
  { value: "DWORD", label: "DWORD", bytes: 4, tagType: "64-bit integer" },
  { value: "DINT", label: "DINT", bytes: 4, tagType: "64-bit integer" },
  { value: "REAL", label: "REAL", bytes: 4, tagType: "64-bit floating point" },
]);

const FIELD_TYPES = new Map(S7_FIELD_TYPES.map((type) => [type.value, type]));
let nextDataBlockKey = 1;
let nextFieldKey = 1;

const integer = (value) => {
  const text = String(value ?? "").trim();
  return text === "" ? Number.NaN : Number(text);
};

const requireInteger = (value, label, min, max) => {
  const parsed = integer(value);
  return Number.isInteger(parsed) && parsed >= min && parsed <= max
    ? ""
    : `${label} must be a whole number from ${min} to ${max}.`;
};

const appendError = (current, next) => (current ? `${current} ${next}` : next);

export function validateS7BindAddress(value) {
  const address = String(value || "");
  if (!address) return "Bind IP address is required.";
  if (address !== address.trim()) return "Bind address cannot contain surrounding whitespace.";
  if (address.includes("%")) return "Bind address cannot include an IPv6 interface zone.";
  if (address === "0.0.0.0" || address === "::" || address === "localhost") {
    return "Use one exact literal unicast or loopback IP address; wildcard and hostname binds are rejected.";
  }
  if (!address.includes(":") && !/^\d+(?:\.\d+){3}$/.test(address)) {
    return "Bind address must be one literal IPv4 or IPv6 address, not a hostname.";
  }
  const error = validateModbusHost(address);
  return error ? `Bind IP address is invalid. ${error}` : "";
}

export function validateS7SourceCIDR(value) {
  const cidr = String(value || "");
  if (!cidr) return "Source CIDR is required.";
  if (cidr !== cidr.trim()) return "Source CIDR cannot contain surrounding whitespace.";
  const separator = cidr.lastIndexOf("/");
  if (separator <= 0) return "Source CIDR must include a prefix length.";
  const address = cidr.slice(0, separator);
  const prefix = Number(cidr.slice(separator + 1));
  const max = address.includes(":") ? 128 : 32;
  if (!Number.isInteger(prefix) || prefix < 1 || prefix > max) {
    return `Source CIDR prefix must be a whole number from 1 to ${max}; catch-all prefixes are rejected.`;
  }
  const addressError = validateS7BindAddress(address);
  if (addressError) return addressError.replace(/^Bind/, "Source");
  if (!address.includes(":")) {
    const octets = address.split(".").map(Number);
    const numeric = octets.reduce((value, octet) => value * 256 + octet, 0);
    const hostSpan = 2 ** (32 - prefix);
    if (numeric % hostSpan !== 0) {
      return "Source CIDR must use the canonical network address with host bits cleared.";
    }
  }
  return "";
}

export function parseS7SourceCIDRs(value) {
  return String(value || "")
    .split(/[\n,]+/)
    .map((entry) => entry.trim())
    .filter(Boolean);
}

export function createS7Field(overrides = {}) {
  const type = FIELD_TYPES.has(overrides.type) ? overrides.type : "BOOL";
  return {
    key: overrides.key || `s7-field-${nextFieldKey++}`,
    byteOffset: String(overrides.byteOffset ?? overrides.byte_offset ?? 0),
    bitOffset: String(overrides.bitOffset ?? overrides.bit_offset ?? 0),
    type,
    writable: overrides.writable === true,
    tagPath: String(overrides.tagPath ?? overrides.tag_path ?? ""),
    tagType: String(overrides.tagType ?? overrides.tag_type ?? ""),
  };
}

export function createS7DataBlock(overrides = {}) {
  return {
    key: overrides.key || `s7-db-${nextDataBlockKey++}`,
    number: String(overrides.number ?? 1),
    size: String(overrides.size ?? 16),
    description: String(overrides.description ?? ""),
    fields: (Array.isArray(overrides.fields) ? overrides.fields : []).map(createS7Field),
  };
}

export function s7ServerFormFromConfig(config = {}) {
  return {
    enabled: config.enabled === true,
    bindAddress: String(config.bind_address || "127.0.0.1"),
    port: String(config.port ?? 1102),
    benchPort: config.bench_port !== false,
    sourceCIDRs: (Array.isArray(config.source_cidrs) ? config.source_cidrs : ["127.0.0.1/32"]).join("\n"),
    calledTSAP: String(config.called_tsap || "0102").toLowerCase(),
    pduCap: String(config.pdu_cap ?? 480),
    maxClients: String(config.max_clients ?? 8),
    maxItems: String(config.max_items ?? 8),
    acceptRate: String(config.accept_rate_per_second ?? 5),
    acceptBurst: String(config.accept_burst ?? 10),
    requestRate: String(config.request_rate_per_second ?? 100),
    requestBurst: String(config.request_burst ?? 200),
    writeRate: String(config.write_rate_per_second ?? 20),
    writeBurst: String(config.write_burst ?? 40),
    handshakeTimeoutMS: String(config.handshake_timeout_ms ?? 5000),
    setupTimeoutMS: String(config.setup_timeout_ms ?? 5000),
    progressTimeoutMS: String(config.progress_timeout_ms ?? 2000),
    idleTimeoutMS: String(config.idle_timeout_ms ?? 120000),
    writeTimeoutMS: String(config.write_timeout_ms ?? 250),
    drainTimeoutMS: String(config.drain_timeout_ms ?? 2000),
    dataBlocks: (Array.isArray(config.data_blocks) ? config.data_blocks : []).map(createS7DataBlock),
  };
}

export function buildS7ServerPayload(form) {
  return {
    enabled: form?.enabled === true,
    bind_address: String(form?.bindAddress || "").trim(),
    port: integer(form?.port),
    bench_port: form?.benchPort === true,
    source_cidrs: parseS7SourceCIDRs(form?.sourceCIDRs),
    called_tsap: String(form?.calledTSAP || "").trim().toLowerCase(),
    pdu_cap: integer(form?.pduCap),
    max_clients: integer(form?.maxClients),
    max_items: integer(form?.maxItems),
    accept_rate_per_second: integer(form?.acceptRate),
    accept_burst: integer(form?.acceptBurst),
    request_rate_per_second: integer(form?.requestRate),
    request_burst: integer(form?.requestBurst),
    write_rate_per_second: integer(form?.writeRate),
    write_burst: integer(form?.writeBurst),
    handshake_timeout_ms: integer(form?.handshakeTimeoutMS),
    setup_timeout_ms: integer(form?.setupTimeoutMS),
    progress_timeout_ms: integer(form?.progressTimeoutMS),
    idle_timeout_ms: integer(form?.idleTimeoutMS),
    write_timeout_ms: integer(form?.writeTimeoutMS),
    drain_timeout_ms: integer(form?.drainTimeoutMS),
    data_blocks: (form?.dataBlocks || []).map((db) => ({
      number: integer(db.number),
      size: integer(db.size),
      description: String(db.description || "").trim(),
      fields: (db.fields || []).map((field) => ({
        byte_offset: integer(field.byteOffset),
        ...(field.type === "BOOL" ? { bit_offset: integer(field.bitOffset) } : {}),
        type: field.type,
        writable: field.writable === true,
        tag_path: String(field.tagPath || "").trim(),
      })),
    })),
  };
}

const isLoopback = (address) => address === "127.0.0.1" || address === "::1";

const fieldBitRange = (field) => {
  const type = FIELD_TYPES.get(field?.type);
  const byteOffset = integer(field?.byteOffset);
  const bitOffset = field?.type === "BOOL" ? integer(field?.bitOffset) : 0;
  if (!type || !Number.isInteger(byteOffset) || !Number.isInteger(bitOffset)) return null;
  const start = byteOffset * 8 + bitOffset;
  const width = field.type === "BOOL" ? 1 : type.bytes * 8;
  return { start, end: start + width - 1 };
};

export function validateS7ServerForm(form) {
  const sourceCIDRs = parseS7SourceCIDRs(form?.sourceCIDRs);
  const port = integer(form?.port);
  const formErrors = {
    bindAddress: validateS7BindAddress(form?.bindAddress),
    port: requireInteger(form?.port, "TCP port", 1, 65535),
    sourceCIDRs: sourceCIDRs.map(validateS7SourceCIDR).find(Boolean) || "",
    calledTSAP: /^[0-9a-fA-F]{4}$/.test(String(form?.calledTSAP || "").trim())
      ? ""
      : "Called TSAP must be exactly four hexadecimal characters.",
    pduCap: [240, 480, 960].includes(integer(form?.pduCap))
      ? ""
      : "PDU cap must be 240, 480, or 960 bytes.",
    maxClients: requireInteger(form?.maxClients, "Maximum clients", 1, 64),
    maxItems: requireInteger(form?.maxItems, "Items per request", 1, 16),
    acceptRate: requireInteger(form?.acceptRate, "Accepted clients per second", 1, 20),
    acceptBurst: requireInteger(form?.acceptBurst, "Accepted-client burst", 1, 40),
    requestRate: requireInteger(form?.requestRate, "Requests per second", 1, 1000),
    requestBurst: requireInteger(form?.requestBurst, "Request burst", 1, 2000),
    writeRate: requireInteger(form?.writeRate, "Writes per second", 1, 100),
    writeBurst: requireInteger(form?.writeBurst, "Write burst", 1, 200),
    handshakeTimeoutMS: requireInteger(form?.handshakeTimeoutMS, "Handshake timeout", 1000, 30000),
    setupTimeoutMS: requireInteger(form?.setupTimeoutMS, "Setup timeout", 1000, 30000),
    progressTimeoutMS: requireInteger(form?.progressTimeoutMS, "Progress timeout", 250, 10000),
    idleTimeoutMS: requireInteger(form?.idleTimeoutMS, "Idle timeout", 5000, 3600000),
    writeTimeoutMS: requireInteger(form?.writeTimeoutMS, "Write timeout", 10, 2000),
    drainTimeoutMS: requireInteger(form?.drainTimeoutMS, "Drain timeout", 100, 10000),
  };
  if (!formErrors.sourceCIDRs && new Set(sourceCIDRs).size !== sourceCIDRs.length) {
    formErrors.sourceCIDRs = "Source CIDRs cannot be duplicated.";
  }
  if (!formErrors.port) {
    if (port === 102 && form?.benchPort) formErrors.port = "TCP/102 is the production port and cannot be marked as a bench port.";
    if (port !== 102 && (port < 1024 || form?.benchPort !== true)) formErrors.port = "Use TCP/102, or mark a high port from 1024 through 65535 as a bench port.";
  }
  if (form?.enabled && !isLoopback(String(form?.bindAddress || "").trim()) && sourceCIDRs.length === 0) {
    formErrors.sourceCIDRs = "An enabled non-loopback listener requires at least one exact source CIDR.";
  }
  for (const [rateField, burstField] of [["acceptRate", "acceptBurst"], ["requestRate", "requestBurst"], ["writeRate", "writeBurst"]]) {
    if (!formErrors[rateField] && !formErrors[burstField] && integer(form[burstField]) < integer(form[rateField])) {
      formErrors[burstField] = "Burst must be greater than or equal to its per-second rate.";
    }
  }

  const dataBlocks = Array.isArray(form?.dataBlocks) ? form.dataBlocks : [];
  const dataBlockErrors = dataBlocks.map(() => "");
  const fieldErrors = dataBlocks.map((db) => (db.fields || []).map(() => ""));
  if (dataBlocks.length > 64) formErrors.dataBlocks = "At most 64 data blocks are allowed.";
  let totalBytes = 0;
  let totalFields = 0;
  const numbers = new Map();
  const tagPaths = new Map();
  dataBlocks.forEach((db, dbIndex) => {
    let error = "";
    const numberError = requireInteger(db.number, "DB number", 1, 65535);
    const sizeError = requireInteger(db.size, "DB size", 1, 4096);
    if (numberError) error = appendError(error, numberError);
    if (sizeError) error = appendError(error, sizeError);
    if (String(db.description || "").trim().length > 256) error = appendError(error, "Description cannot exceed 256 characters.");
    const number = integer(db.number);
    if (Number.isInteger(number)) {
      if (numbers.has(number)) {
        const prior = numbers.get(number);
        dataBlockErrors[prior] = appendError(dataBlockErrors[prior], `DB${number} is duplicated.`);
        error = appendError(error, `DB${number} is duplicated.`);
      } else numbers.set(number, dbIndex);
    }
    if (!sizeError) totalBytes += integer(db.size);
    const fields = Array.isArray(db.fields) ? db.fields : [];
    totalFields += fields.length;
    fields.forEach((field, fieldIndex) => {
      let fieldError = "";
      const type = FIELD_TYPES.get(field.type);
      if (!type) fieldError = appendError(fieldError, "Select a supported S7 scalar type.");
      const byteError = requireInteger(field.byteOffset, "Byte offset", 0, 4095);
      if (byteError) fieldError = appendError(fieldError, byteError);
      if (field.type === "BOOL") {
        const bitError = requireInteger(field.bitOffset, "Bit offset", 0, 7);
        if (bitError) fieldError = appendError(fieldError, bitError);
      } else if (integer(field.bitOffset) !== 0) {
        fieldError = appendError(fieldError, "Only BOOL mappings may use a bit offset.");
      }
      const tagPath = String(field.tagPath || "").trim();
      if (!tagPath) fieldError = appendError(fieldError, "Control Seat tag path is required.");
      if (tagPath.length > 1024) fieldError = appendError(fieldError, "Tag path cannot exceed 1024 bytes.");
      if (tagPath) {
        if (tagPaths.has(tagPath)) {
          const prior = tagPaths.get(tagPath);
          fieldErrors[prior.db][prior.field] = appendError(fieldErrors[prior.db][prior.field], "A tag can be exposed only once.");
          fieldError = appendError(fieldError, "A tag can be exposed only once.");
        } else tagPaths.set(tagPath, { db: dbIndex, field: fieldIndex });
      }
      const range = fieldBitRange(field);
      const dbSize = integer(db.size);
      if (range && Number.isInteger(dbSize) && range.end >= dbSize * 8) {
        fieldError = appendError(fieldError, `The ${field.type} value extends past the end of DB${number || ""}.`);
      }
      fieldErrors[dbIndex][fieldIndex] = fieldError;
    });
    for (let left = 0; left < fields.length; left += 1) {
      const leftRange = fieldBitRange(fields[left]);
      if (!leftRange) continue;
      for (let right = left + 1; right < fields.length; right += 1) {
        const rightRange = fieldBitRange(fields[right]);
        if (!rightRange) continue;
        if (leftRange.start <= rightRange.end && rightRange.start <= leftRange.end) {
          fieldErrors[dbIndex][left] = appendError(fieldErrors[dbIndex][left], "Address overlaps another field in this data block.");
          fieldErrors[dbIndex][right] = appendError(fieldErrors[dbIndex][right], "Address overlaps another field in this data block.");
        }
      }
    }
    dataBlockErrors[dbIndex] = appendError(dataBlockErrors[dbIndex], error);
  });
  if (totalBytes > 256 * 1024) formErrors.dataBlocks = appendError(formErrors.dataBlocks, "The total data-block image cannot exceed 256 KiB.");
  if (totalFields > 1024) formErrors.dataBlocks = appendError(formErrors.dataBlocks, "At most 1,024 mapped fields are allowed.");

  const firstFormError = Object.values(formErrors).find(Boolean) || "";
  const firstDBError = dataBlockErrors.find(Boolean) || "";
  const firstFieldError = fieldErrors.flat().find(Boolean) || "";
  return {
    valid: !firstFormError && !firstDBError && !firstFieldError,
    message: firstFormError || firstDBError || firstFieldError,
    formErrors,
    dataBlockErrors,
    fieldErrors,
  };
}

export function normalizeS7ServerStatus(status = {}) {
  const runtime = status.runtime && typeof status.runtime === "object" ? status.runtime : {};
  const metrics = runtime.metrics && typeof runtime.metrics === "object" ? runtime.metrics : {};
  const itemReturns = metrics.item_returns && typeof metrics.item_returns === "object" ? metrics.item_returns : {};
  const writeStages = metrics.write_stages && typeof metrics.write_stages === "object" ? metrics.write_stages : {};
  const qualityRejections = Array.isArray(metrics.quality_rejections) ? metrics.quality_rejections : [];
  return {
    profile: String(status.profile || "s7comm-server-v1"),
    available: status.available === true,
    executable: status.executable === true,
    mode: String(status.mode || "configuration_only"),
    lifecycle: String(status.lifecycle || "stopped"),
    desiredRevision: Number(status.desired_revision || 0),
    desiredDigest: String(status.desired_digest || ""),
    desiredIntegrityProfile: String(status.desired_integrity_profile || ""),
    desiredSealed: status.desired_sealed === true,
    appliedRevision: Number(status.applied_revision || 0),
    appliedDigest: String(status.applied_digest || ""),
    appliedIntegrityProfile: String(status.applied_integrity_profile || ""),
    appliedSealed: status.applied_sealed === true,
    desiredEqualsApplied: status.desired_equals_applied === true,
    runtimeMatchesApplied: status.runtime_matches_applied === true,
    reconciled: status.reconciled === true,
    repairRequired: status.repair_required === true,
    warnings: Array.isArray(status.warnings) ? status.warnings.map(String) : [],
    writeStateContract: String(status.write_state_contract || ""),
    lastError: String(status.last_error || runtime.last_error || ""),
    runtime: {
      available: runtime.available === true,
      executable: runtime.executable === true,
      lifecycle: String(runtime.lifecycle || "stopped"),
      enabled: runtime.enabled === true,
      listening: runtime.listening === true,
      revision: Number(runtime.revision || 0),
      digest: String(runtime.digest || ""),
      bindAddress: String(runtime.bind_address || ""),
      port: Number(runtime.port || 0),
      calledTSAP: String(runtime.called_tsap || ""),
      sourceCIDRCount: Number(runtime.source_cidr_count || 0),
      pduCap: Number(runtime.pdu_cap || 0),
      maxClients: Number(runtime.max_clients || 0),
      dbCount: Number(runtime.db_count || 0),
      mappingCount: Number(runtime.mapping_count || 0),
      startedAt: runtime.started_at || "",
      metrics: {
        activeClients: Number(metrics.active_clients || 0),
        peakClients: Number(metrics.peak_clients || 0),
        handshakeAwaitCOTP: Number(metrics.handshake_await_cotp || 0),
        handshakeAwaitSetup: Number(metrics.handshake_await_setup || 0),
        handshakeActive: Number(metrics.handshake_active || 0),
        cotpHandshakes: Number(metrics.cotp_handshakes || 0),
        acceptedSessions: Number(metrics.accepted_sessions || 0),
        rejectedByCIDR: Number(metrics.rejected_by_cidr || 0),
        rejectedByLimit: Number(metrics.rejected_by_limit || 0),
        rateLimitedSessions: Number(metrics.rate_limited_sessions || 0),
        protocolErrors: Number(metrics.protocol_errors || 0),
        malformedFrames: Number(metrics.malformed_frames || 0),
        unsupportedFunctions: Number(metrics.unsupported_functions || 0),
        oversizedPDUs: Number(metrics.oversized_pdus || 0),
        timeouts: Number(metrics.timeouts || 0),
        transportErrors: Number(metrics.transport_errors || 0),
        gracefulDisconnects: Number(metrics.graceful_disconnects || 0),
        setupNegotiations: Number(metrics.setup_negotiations || 0),
        readRequests: Number(metrics.read_requests || 0),
        writeRequests: Number(metrics.write_requests || 0),
        bytesReceived: Number(metrics.bytes_received || 0),
        bytesSent: Number(metrics.bytes_sent || 0),
        requestsPerSecond: Number(metrics.requests_per_second || 0),
        bytesReceivedPerSecond: Number(metrics.bytes_received_per_second || 0),
        bytesSentPerSecond: Number(metrics.bytes_sent_per_second || 0),
        itemReturns: {
          internal: Number(itemReturns.internal || 0),
          denied: Number(itemReturns.denied || 0),
          address: Number(itemReturns.address || 0),
          unsupported: Number(itemReturns.unsupported || 0),
          type: Number(itemReturns.type || 0),
          object: Number(itemReturns.object || 0),
          success: Number(itemReturns.success || 0),
        },
        goodConversions: Number(metrics.good_conversions || 0),
        nonGoodConversions: Number(metrics.non_good_conversions || 0),
        writeStages: {
          requested: Number(writeStages.requested || 0),
          ownershipAccepted: Number(writeStages.ownership_accepted || 0),
          ownershipRejected: Number(writeStages.ownership_rejected || 0),
          connectorAccepted: Number(writeStages.connector_accepted || 0),
          auditRecorded: Number(writeStages.audit_recorded || 0),
          auditFailures: Number(writeStages.audit_failures || 0),
          independentlyObserved: Number(writeStages.independently_observed || 0),
        },
        qualityRejections: qualityRejections.map((entry) => ({
          tagPath: String(entry?.tag_path || ""),
          missing: Number(entry?.missing || 0),
          nonGood: Number(entry?.non_good || 0),
          stale: Number(entry?.stale || 0),
          conversion: Number(entry?.conversion || 0),
          snapshotUnavailable: Number(entry?.snapshot_unavailable || 0),
          lastRejectedAt: entry?.last_rejected_at || "",
        })).filter((entry) => entry.tagPath),
        negotiatedPDU240: Number(metrics.negotiated_pdu_240 || 0),
        negotiatedPDU480: Number(metrics.negotiated_pdu_480 || 0),
        negotiatedPDU960: Number(metrics.negotiated_pdu_960 || 0),
        lastAcceptedAt: metrics.last_accepted_at || "",
        lastRejectedAt: metrics.last_rejected_at || "",
        lastSuccessfulReadAt: metrics.last_successful_read_at || "",
        lastRejectedReadAt: metrics.last_rejected_read_at || "",
        lastAcceptedWriteAt: metrics.last_accepted_write_at || "",
        lastRejectedWriteAt: metrics.last_rejected_write_at || "",
        lastAuditRecordedAt: metrics.last_audit_recorded_at || "",
        lastAuditFailureAt: metrics.last_audit_failure_at || "",
        lastProtocolErrorAt: metrics.last_protocol_error_at || "",
        lastTransportErrorAt: metrics.last_transport_error_at || "",
      },
    },
  };
}

export function s7RollbackSuccessNotice(targetRevision, newRevision, normalizedStatus = {}) {
  const revision = Number(newRevision || 0);
  const appliedAndReconciled = normalizedStatus.reconciled === true
    && revision > 0
    && Number(normalizedStatus.desiredRevision || 0) === revision
    && Number(normalizedStatus.appliedRevision || 0) === revision;
  if (appliedAndReconciled) {
    return {
      type: "success",
      title: "S7Comm settings restored",
      message: "The selected settings are active. Your previous saved settings remain available.",
    };
  }
  return {
    type: "warning",
    title: "S7Comm settings saved",
    message: "The selected settings were saved, but the server has not confirmed they are active. Check the server status before relying on them.",
  };
}

const sanitizeS7PublicValue = (value) => {
  if (Array.isArray(value)) return value.map(sanitizeS7PublicValue);
  if (!value || typeof value !== "object") return value;
  return Object.fromEntries(Object.entries(value)
    .filter(([key]) => !["tag_id", "tagId", "TagID"].includes(key))
    .map(([key, entry]) => [key, sanitizeS7PublicValue(entry)]));
};

const ROLLBACK_BLOCKER_LABELS = Object.freeze({
  never_applied: "These settings were never active",
  already_desired: "These settings are already selected",
  legacy_unsealed: "This older configuration cannot be restored",
  target_configuration_only: "Enabled settings cannot run on this computer",
  mapping_unavailable: "One or more mapped tag paths are unavailable",
  target_validation_failed: "These settings are not valid on this computer",
  integrity_unavailable: "These settings cannot be verified",
});

export function s7RollbackBlockerLabel(code) {
  return ROLLBACK_BLOCKER_LABELS[String(code || "")] || "These settings cannot be restored";
}

export function normalizeS7RevisionHistory(payload = {}) {
  const revisions = Array.isArray(payload.revisions) ? payload.revisions : [];
  return {
    profile: String(payload.profile || "s7comm-server-v1"),
    revisions: revisions.slice(0, 100).map((revision) => ({
      revision: Number(revision?.revision || 0),
      digest: String(revision?.digest || ""),
      enabled: revision?.enabled === true,
      bindAddress: String(revision?.bind_address || ""),
      port: Number(revision?.port || 0),
      calledTSAP: String(revision?.called_tsap || ""),
      dataBlockCount: Number(revision?.data_block_count || 0),
      mappingCount: Number(revision?.mapping_count || 0),
      writableMappingCount: Number(revision?.writable_mapping_count || 0),
      rollbackOf: revision?.rollback_of == null ? null : Number(revision.rollback_of),
      createdAt: revision?.created_at || "",
      appliedAt: revision?.applied_at || "",
      integrityProfile: String(revision?.integrity_profile || ""),
      sealed: revision?.sealed === true,
      desired: revision?.desired === true,
      applied: revision?.applied === true,
      rollbackEligible: revision?.rollback_eligible === true,
      rollbackBlocker: String(revision?.rollback_blocker || ""),
    })).filter((revision) => revision.revision > 0),
  };
}

const CONFIG_AUDIT_ACTIONS = new Set(["configure", "enable", "disable"]);
const CONFIG_AUDIT_OUTCOMES = new Set([
  "proposed", "previewed", "confirmed", "desired_only", "applied",
  "rejected", "apply_failed", "expired",
]);
const CONFIG_AUDIT_STAGES = new Set(["preview", "confirmation", "persist", "apply"]);

export function normalizeS7ConfigAudit(payload = {}) {
  const operations = Array.isArray(payload.operations) ? payload.operations : [];
  return {
    profile: String(payload.profile || "s7comm-server-v1"),
    operations: operations.slice(0, 100).map((operation) => {
      const action = String(operation?.action || "");
      const outcome = String(operation?.outcome || "");
      const failureStage = String(operation?.failure_stage || "");
      return {
        actorId: String(operation?.actor_id || "").slice(0, 256),
        actorName: String(operation?.actor_name || "").slice(0, 256),
        action: CONFIG_AUDIT_ACTIONS.has(action) ? action : "configure",
        outcome: CONFIG_AUDIT_OUTCOMES.has(outcome) ? outcome : "rejected",
        requestedEnabled: operation?.requested_enabled === true,
        priorEnabled: typeof operation?.prior_enabled === "boolean" ? operation.prior_enabled : null,
        baseRevision: Number(operation?.base_revision || 0),
        candidateDigest: String(operation?.candidate_digest || ""),
        revision: Number(operation?.revision || 0),
        confirmationRequired: operation?.confirmation_required === true,
        noAuthenticationAcknowledged: operation?.no_authentication_acknowledged === true,
        dataBlockCount: Number(operation?.data_block_count || 0),
        mappingCount: Number(operation?.mapping_count || 0),
        semanticChangeCount: Number(operation?.semantic_change_count || 0),
        failureStage: CONFIG_AUDIT_STAGES.has(failureStage) ? failureStage : "",
        failureCode: String(operation?.failure_code || ""),
        proposedAt: operation?.proposed_at || "",
        previewedAt: operation?.previewed_at || "",
        confirmedAt: operation?.confirmed_at || "",
        completedAt: operation?.completed_at || "",
        expiresAt: operation?.expires_at || "",
      };
    }).filter((operation) => operation.actorId && operation.proposedAt),
  };
}

export function normalizeS7SemanticDiff(changes = []) {
  return (Array.isArray(changes) ? changes : []).map((change) => ({
    operation: ["added", "removed", "changed"].includes(change?.operation) ? change.operation : "changed",
    path: String(change?.path || ""),
    before: sanitizeS7PublicValue(change?.before),
    after: sanitizeS7PublicValue(change?.after),
  })).filter((change) => change.path);
}

export function normalizeS7RollbackPreview(preview = {}) {
  return {
    profile: String(preview.profile || "s7comm-server-v1"),
    targetRevision: Number(preview.target_revision || 0),
    targetDigest: String(preview.target_digest || ""),
    baseRevision: Number(preview.base_revision || 0),
    config: preview.config && typeof preview.config === "object" ? sanitizeS7PublicValue(preview.config) : {},
    digest: String(preview.digest || ""),
    semanticDiff: normalizeS7SemanticDiff(preview.semantic_diff),
    warnings: Array.isArray(preview.warnings) ? preview.warnings.map(String) : [],
    confirmationRequired: preview.confirmation_required === true,
    noAuthenticationAcknowledgementRequired: preview.no_authentication_acknowledgement_required === true,
  };
}

export function formatS7SemanticValue(value) {
  if (value == null) return "Not present";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return JSON.stringify(sanitizeS7PublicValue(value), null, 2);
}
